import { useContext, useEffect, useState } from 'react';
import {
  Box, Button, Divider, FormControlLabel, IconButton, Paper, Stack, Switch, TextField, Typography,
} from '@mui/material';
import { AddOutlined, DeleteOutline } from '@mui/icons-material';
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import SurveyAdminService from '../../../shared/services/survey/survey-admin.service';
import { CalcQuestionSelect } from '../../presentation-components/surveys/builder/calc-question-select';
import { ConditionBuilder } from '../../presentation-components/surveys/builder/condition-builder';
import { ConversionFactorPicker, ConversionSelection } from '../../presentation-components/surveys/builder/conversion-factor-picker';
import { LinearFormulaEditor } from '../../presentation-components/surveys/builder/linear-formula-editor';
import { useConversionYearFromReportingSelection } from '../../presentation-components/surveys/builder/use-conversion-year';
import { sanitizeConditions, choiceOptionsLookupFor } from '../../../shared/utils/survey-rules/condition-utils';
import {
  LinearFormula,
  defaultLinearFormula,
  extractAliasRefs,
  formulaToText,
  linearToTree,
  normalizeFormula,
  treeToLinear,
} from '../../../shared/utils/survey-rules/formula';
import { defaultPathForQuestion, firstNumericItem, pathLabel } from '../../../shared/utils/survey-rules/question-eligibility';
import {
  ICreateEmissionRule,
  IEmissionRuleFormula,
  IEmissionRuleInput,
  ISurveyQuestionGroupEmissionRule,
  ISurveyQuestionGroupItem,
  IVisibilityCondition,
} from '../../../shared/services/survey/survey.model';
import { RuleSaveApi, SimpleRuleDraft } from './simple-rule-dialog';

interface Props {
  open: boolean;
  onClose: () => void;
  successHandler: () => void;
  groupId: string;
  groupItems: ISurveyQuestionGroupItem[];
  existingRule?: ISurveyQuestionGroupEmissionRule;
  /** Adjustment mode: the rule produces a named intermediate value instead of an emission. */
  isAdjustment?: boolean;
  /** Value names from already-defined intermediate calculations, available as inputs. */
  adjustmentAliases?: string[];
  api?: RuleSaveApi;
  /** Seed handed over by the simple dialog's "Switch to advanced formula". */
  initialDraft?: SimpleRuleDraft;
}

/**
 * Formula-builder rule dialog: several inputs, step-by-step formula, optional factor
 * and conditions. Also authors intermediate (adjustment) values via isAdjustment.
 * A saved formula the step editor cannot represent is kept untouched unless the
 * user explicitly rebuilds it.
 */
export const AdvancedRuleDialog = ({
  open, onClose, successHandler, groupId, groupItems, existingRule,
  isAdjustment = false, adjustmentAliases = [], api, initialDraft,
}: Props) => {
  const surveyAdminService = SurveyAdminService.getInstance();
  const saveApi = api ?? {
    createEmissionRule: (gid: string, payload: ICreateEmissionRule) => surveyAdminService.createEmissionRule(gid, payload),
    updateEmissionRule: (gid: string, rid: string, payload: ICreateEmissionRule) => surveyAdminService.updateEmissionRule(gid, rid, payload),
  };
  const userNotificationsContext = useContext(UserNotificationsContext);
  const reportingYear = useConversionYearFromReportingSelection();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [name, setName] = useState('');
  const [outputAlias, setOutputAlias] = useState('');
  const [inputs, setInputs] = useState<IEmissionRuleInput[]>([]);
  const [linearFormula, setLinearFormula] = useState<LinearFormula>(defaultLinearFormula());
  // A formula tree the step editor cannot represent is held here verbatim and submitted
  // unchanged, so name/factor/condition edits never destroy it.
  const [originalFormula, setOriginalFormula] = useState<IEmissionRuleFormula | null>(null);
  const [rebuildingFormula, setRebuildingFormula] = useState(false);
  const [useConditions, setUseConditions] = useState(false);
  const [conditions, setConditions] = useState<IVisibilityCondition[]>([]);
  const [selection, setSelection] = useState<ConversionSelection | null>(null);

  const isEditMode = !!existingRule;
  const keepingOriginalFormula = originalFormula !== null && !rebuildingFormula;

  useEffect(() => {
    if (!open) return;
    if (existingRule) {
      setName(existingRule.name);
      setOutputAlias(existingRule.outputAlias ?? '');
      const nextInputs = (existingRule.inputs ?? []).map(i => ({ ...i }));
      setInputs(nextInputs);
      const nextFormula = existingRule.formula;
      const asLinear = nextFormula ? treeToLinear(nextFormula) : null;
      if (asLinear) {
        setLinearFormula(asLinear);
        setOriginalFormula(null);
      } else {
        setLinearFormula(defaultLinearFormula());
        setOriginalFormula(nextFormula ?? null);
      }
      const aw = existingRule.scopeMetadata?.applyWhen?.allOf;
      setUseConditions(!!aw && aw.length > 0);
      setConditions(aw && aw.length ? aw.map(c => ({ ...c })) : []);
    } else if (initialDraft) {
      setName(initialDraft.name);
      setOutputAlias('');
      setInputs([{
        alias: initialDraft.alias,
        kind: 'question',
        questionId: initialDraft.questionId || firstNumericItem(groupItems)?.surveyQuestionId || '',
        path: initialDraft.path || 'numericValue',
      }]);
      setLinearFormula({ startKind: 'alias', startAlias: initialDraft.alias, startLiteral: undefined, steps: [] });
      setOriginalFormula(null);
      setUseConditions(initialDraft.useConditions);
      setConditions(initialDraft.conditions.map(c => ({ ...c })));
    } else {
      setName('');
      setOutputAlias('');
      setInputs([]);
      setLinearFormula(defaultLinearFormula());
      setOriginalFormula(null);
      setUseConditions(false);
      setConditions([]);
    }
    setRebuildingFormula(false);
    setSelection(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existingRule, initialDraft]);

  const calendarYear = selection?.conversion?.calendarYear
    ? String(selection.conversion.calendarYear)
    : reportingYear;

  const addQuestionInput = () => {
    setInputs(prev => [...prev, {
      alias: `q${prev.length + 1}`,
      kind: 'question',
      questionId: firstNumericItem(groupItems)?.surveyQuestionId ?? '',
      path: defaultPathForQuestion(firstNumericItem(groupItems)),
    }]);
  };
  const addLiteralInput = () => {
    setInputs(prev => [...prev, { alias: `k${prev.length + 1}`, kind: 'literal', literalValue: 1 }]);
  };
  const addDateInput = () => {
    setInputs(prev => [...prev, { alias: `d${prev.length + 1}`, kind: 'date', dateValue: '' }]);
  };
  const removeInput = (index: number) => setInputs(prev => prev.filter((_, i) => i !== index));
  const updateInput = (index: number, patch: Partial<IEmissionRuleInput>) => {
    setInputs(prev => prev.map((inp, i) => (i === index ? { ...inp, ...patch } : inp)));
  };

  const initialConversionId = open && !isAdjustment
    ? (existingRule?.conversionId ?? initialDraft?.conversionId ?? null)
    : null;

  const handleSubmit = async () => {
    if (!name.trim()) return;
    setIsSubmitting(true);
    try {
      const sanitizedInputs: IEmissionRuleInput[] = inputs.map(inp => {
        if (inp.kind === 'literal') {
          const n = Number(inp.literalValue);
          if (!Number.isFinite(n)) throw new Error('Each constant input must have a valid number.');
          return { alias: inp.alias.trim(), kind: 'literal', literalValue: n };
        }
        if (inp.kind === 'date') {
          if (!inp.dateValue) throw new Error('Each date input must have a date selected.');
          return { alias: inp.alias.trim(), kind: 'date', dateValue: inp.dateValue };
        }
        if (inp.kind === 'adjustmentRef') {
          return { alias: inp.alias.trim(), kind: 'adjustmentRef' as const };
        }
        return {
          alias: inp.alias.trim(),
          questionId: inp.questionId!,
          path: inp.path || 'numericValue',
        };
      });

      if (sanitizedInputs.length === 0) {
        throw new Error('Add at least one input (question value or numeric constant).');
      }

      const effectiveFormula = keepingOriginalFormula && originalFormula
        ? originalFormula
        : normalizeFormula(linearToTree(linearFormula));

      // Values from intermediate calculations referenced in the formula are included
      // automatically, without the user adding them by hand.
      const existingAliases = new Set(sanitizedInputs.map(i => i.alias));
      const usedAdjRefs = [...new Set(
        extractAliasRefs(effectiveFormula).filter(a => adjustmentAliases.includes(a) && !existingAliases.has(a)),
      )];
      for (const alias of usedAdjRefs) {
        sanitizedInputs.push({ alias, kind: 'adjustmentRef' as const });
      }

      if (isAdjustment && !outputAlias.trim()) {
        throw new Error('A value name is required for intermediate calculations.');
      }

      const payload: ICreateEmissionRule = {
        name: name.trim(),
        inputs: sanitizedInputs,
        formula: effectiveFormula,
        scopeMetadata: useConditions && conditions.length > 0
          ? { applyWhen: { allOf: sanitizeConditions(conditions, choiceOptionsLookupFor(groupItems)) } }
          : null,
        isAdjustment: isAdjustment || undefined,
        outputAlias: isAdjustment ? outputAlias.trim() : undefined,
        conversionId: isAdjustment ? undefined : (selection?.conversionId || undefined),
        measurementUnitId: isAdjustment ? undefined : (selection?.measurementUnitId || undefined),
        groupId: isAdjustment ? null : (selection?.conversionId ? (selection.groupId || null) : null),
      };

      if (isEditMode && existingRule) {
        await saveApi.updateEmissionRule(groupId, existingRule.id, payload);
      } else {
        await saveApi.createEmissionRule(groupId, payload);
      }
      userNotificationsContext.openSnackbar({
        open: true,
        message: isEditMode ? 'Rule updated' : 'Rule created',
        backgroundColor: 'primary.dark',
      });
      successHandler();
      onClose();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'An error occurred, please try again';
      userNotificationsContext.openSnackbar({ open: true, message: msg, backgroundColor: 'text.primary' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const title = isAdjustment
    ? (isEditMode ? 'Edit Intermediate Value' : 'Create Intermediate Value')
    : (isEditMode ? 'Edit Advanced Rule' : 'Create Advanced Rule');

  return (
    <DialogWrapper
      open={open}
      onClose={onClose}
      title={title}
      cancelHandler={onClose}
      submitHandler={handleSubmit}
      isSubmitting={isSubmitting}
      disableSaveButton={!name.trim()}
      maxWidth='md'
    >
      <Stack spacing={3} sx={{ mt: 1 }}>
        <TextField
          label='Rule name'
          value={name}
          onChange={e => setName(e.target.value)}
          fullWidth
          required
        />

        {isAdjustment && (
          <TextField
            label='Value name'
            helperText='Other calculations refer to this value by name, for example distanceKm (no spaces)'
            value={outputAlias}
            onChange={e => setOutputAlias(e.target.value.replace(/\s/g, ''))}
            fullWidth
            required
          />
        )}

        <Box>
          <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 1 }}>
            <Typography variant='subtitle2'>Inputs</Typography>
            <Stack direction='row' spacing={1}>
              <Button size='small' startIcon={<AddOutlined />} onClick={addQuestionInput}>From question</Button>
              <Button size='small' startIcon={<AddOutlined />} onClick={addLiteralInput}>Numeric constant</Button>
              <Button size='small' startIcon={<AddOutlined />} onClick={addDateInput}>Date</Button>
            </Stack>
          </Stack>
          {inputs.map((input, index) => (
            <Stack key={index} direction='row' spacing={1} alignItems='center' sx={{ mb: 1 }} flexWrap='wrap'>
              <TextField
                size='small'
                label='Name'
                value={input.alias}
                onChange={e => updateInput(index, { alias: e.target.value })}
                sx={{ width: 120 }}
              />
              {input.kind === 'literal' ? (
                <TextField
                  size='small'
                  label='Constant value'
                  type='number'
                  value={input.literalValue ?? ''}
                  onChange={e => updateInput(index, { literalValue: e.target.value === '' ? undefined : Number(e.target.value) })}
                  sx={{ width: 160 }}
                  inputProps={{ step: 'any' }}
                />
              ) : input.kind === 'date' ? (
                <TextField
                  size='small'
                  label='Date'
                  type='date'
                  value={input.dateValue ?? ''}
                  onChange={e => updateInput(index, { dateValue: e.target.value })}
                  sx={{ width: 180 }}
                  InputLabelProps={{ shrink: true }}
                />
              ) : input.kind === 'adjustmentRef' ? (
                <Typography variant='body2' color='text.secondary' sx={{ alignSelf: 'center' }}>
                  Value from an intermediate calculation
                </Typography>
              ) : (
                <>
                  <CalcQuestionSelect
                    value={input.questionId ?? ''}
                    groupItems={groupItems}
                    onChange={(qid, item) => updateInput(index, { questionId: qid, path: defaultPathForQuestion(item) })}
                  />
                  {input.path && input.path !== 'numericValue' && input.path !== 'textValueAsDate' && (
                    <Typography variant='caption' color='text.secondary'>
                      uses the answer&apos;s {pathLabel(input.path)}
                    </Typography>
                  )}
                </>
              )}
              <IconButton size='small' onClick={() => removeInput(index)}>
                <DeleteOutline fontSize='small' />
              </IconButton>
            </Stack>
          ))}
          {inputs.length === 0 && (
            <Typography variant='body2' color='text.secondary'>
              Add question answers and/or numeric constants to use in the formula.
            </Typography>
          )}
        </Box>

        <Divider />

        <Box>
          <Typography variant='subtitle2' sx={{ mb: 1 }}>Formula</Typography>
          {keepingOriginalFormula ? (
            <Paper variant='outlined' sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant='subtitle2' fontWeight={600} sx={{ mb: 0.5 }}>
                This rule uses an advanced formula
              </Typography>
              <Typography variant='body2' sx={{ fontFamily: 'monospace', fontSize: '0.85rem', mb: 1 }}>
                {formulaToText(originalFormula)}
              </Typography>
              <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 1.5 }}>
                It will be kept exactly as it is unless you choose to rebuild it. You can still change
                the name, inputs, factor and conditions.
              </Typography>
              <Button size='small' variant='outlined' onClick={() => setRebuildingFormula(true)}>
                Rebuild formula
              </Button>
            </Paper>
          ) : (
            <>
              {originalFormula !== null && rebuildingFormula && (
                <Typography variant='caption' color='warning.main' display='block' sx={{ mb: 1.5 }}>
                  Rebuilding replaces the saved formula when you save.
                </Typography>
              )}
              <LinearFormulaEditor
                formula={linearFormula}
                onChange={setLinearFormula}
                inputs={inputs}
                adjustmentAliases={adjustmentAliases}
              />
              {adjustmentAliases.length > 0 && (
                <Typography variant='caption' color='text.secondary' display='block' sx={{ mt: 1.5 }}>
                  Values from intermediate calculations are included automatically when the formula uses them.
                </Typography>
              )}
            </>
          )}
        </Box>

        <Divider />

        <Box>
          <Typography variant='subtitle2' sx={{ mb: 1 }}>Only calculate when conditions are met (optional)</Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
            When enabled, every condition below must match the respondent&apos;s answers before this rule runs.
          </Typography>
          <FormControlLabel
            control={<Switch checked={useConditions} onChange={e => setUseConditions(e.target.checked)} />}
            label='Only calculate when conditions are met'
          />
          {useConditions && (
            <Box sx={{ mt: 2 }}>
              <ConditionBuilder
                conditions={conditions}
                onChange={setConditions}
                candidateItems={groupItems}
              />
            </Box>
          )}
        </Box>

        {!isAdjustment && (
          <>
            <Divider />
            <Box>
              <Typography variant='subtitle2' sx={{ mb: 1 }}>Emission factor (optional)</Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                The formula result is multiplied by this factor to produce kg CO₂e. Without a factor
                the rule only produces an activity amount.
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                Emission factor year:&nbsp;<strong>{calendarYear}</strong>
              </Typography>
              <ConversionFactorPicker
                initialConversionId={initialConversionId}
                onChange={setSelection}
                calendarYear={calendarYear}
              />
            </Box>
          </>
        )}
      </Stack>
    </DialogWrapper>
  );
};
